import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Building2, CreditCard, ArrowLeftRight, Plus, Pencil, Trash2, 
  Wallet, TrendingUp, TrendingDown, Calendar, AlertTriangle, RefreshCw
} from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

export default function TesoreriaAnagrafichePage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [banche, setBanche] = useState([]);
  const [carte, setCarte] = useState([]);
  const [giroconti, setGiroconti] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);
  
  // Dialog states
  const [bancaDialog, setBancaDialog] = useState(false);
  const [cartaDialog, setCartaDialog] = useState(false);
  const [girocontoDialog, setGirocontoDialog] = useState(false);
  const [editingBanca, setEditingBanca] = useState(null);
  const [editingCarta, setEditingCarta] = useState(null);
  
  // Form states
  const [bancaForm, setBancaForm] = useState({
    nome: "", iban: "", bic_swift: "", filiale: "", 
    saldo_iniziale: 0, data_saldo_iniziale: "", note: ""
  });
  const [cartaForm, setCartaForm] = useState({
    nome: "", numero_carta: "", tipologia: "", banca_id: "",
    saldo_iniziale: 0, data_saldo_iniziale: "", data_scadenza: "", note: ""
  });
  const [girocontoForm, setGirocontoForm] = useState({
    data: new Date().toISOString().split('T')[0], importo: 0, tipo: "banca_carta",
    banca_origine_id: "", banca_destinazione_id: "",
    carta_origine_id: "", carta_destinazione_id: "", descrizione: "", note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const prefix = getApiPrefix();
      const [bancheRes, carteRes, girocontiRes, riepilogoRes] = await Promise.all([
        api.get(`${prefix}/banche`),
        api.get(`${prefix}/carte`),
        api.get(`${prefix}/giroconti`),
        api.get(`${prefix}/tesoreria/riepilogo-saldi`)
      ]);
      setBanche(bancheRes.data || []);
      setCarte(carteRes.data || []);
      setGiroconti(girocontiRes.data || []);
      setRiepilogo(riepilogoRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // BANCHE handlers
  const openBancaDialog = (banca = null) => {
    if (banca) {
      setEditingBanca(banca);
      setBancaForm({
        nome: banca.nome || "", iban: banca.iban || "", bic_swift: banca.bic_swift || "",
        filiale: banca.filiale || "", saldo_iniziale: banca.saldo_iniziale || 0,
        data_saldo_iniziale: banca.data_saldo_iniziale || "", note: banca.note || ""
      });
    } else {
      setEditingBanca(null);
      setBancaForm({ nome: "", iban: "", bic_swift: "", filiale: "", saldo_iniziale: 0, data_saldo_iniziale: "", note: "" });
    }
    setBancaDialog(true);
  };

  const saveBanca = async () => {
    if (!bancaForm.nome) { handleApiError({ message: t("errors.requiredBankName") }); return; }
    try {
      const prefix = getApiPrefix();
      if (editingBanca) {
        await api.put(`${prefix}/banche/${editingBanca.id}`, bancaForm);
        showSuccess(t("success.bankUpdated"));
      } else {
        await api.post(`${prefix}/banche`, bancaForm);
        showSuccess(t("success.bankCreated"));
      }
      setBancaDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  const deleteBanca = async (id) => {
    if (!confirm(t("confirm.deactivateBank"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/banche/${id}`);
      showSuccess(t("success.bankDeactivated"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  // CARTE handlers
  const openCartaDialog = (carta = null) => {
    if (carta) {
      setEditingCarta(carta);
      setCartaForm({
        nome: carta.nome || "", numero_carta: carta.numero_carta || "", tipologia: carta.tipologia || "",
        banca_id: carta.banca_id || "", saldo_iniziale: carta.saldo_iniziale || 0,
        data_saldo_iniziale: carta.data_saldo_iniziale || "", data_scadenza: carta.data_scadenza || "", note: carta.note || ""
      });
    } else {
      setEditingCarta(null);
      setCartaForm({ nome: "", numero_carta: "", tipologia: "", banca_id: "", saldo_iniziale: 0, data_saldo_iniziale: "", data_scadenza: "", note: "" });
    }
    setCartaDialog(true);
  };

  const saveCarta = async () => {
    if (!cartaForm.nome) { handleApiError({ message: t("errors.requiredCardName") }); return; }
    try {
      const prefix = getApiPrefix();
      if (editingCarta) {
        await api.put(`${prefix}/carte/${editingCarta.id}`, cartaForm);
        showSuccess(t("success.cardUpdated"));
      } else {
        await api.post(`${prefix}/carte`, cartaForm);
        showSuccess(t("success.cardCreated"));
      }
      setCartaDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  const deleteCarta = async (id) => {
    if (!confirm(t("confirm.deactivateCard"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/carte/${id}`);
      showSuccess(t("success.cardDeactivated"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  // GIROCONTI handlers
  const openGirocontoDialog = () => {
    setGirocontoForm({
      data: new Date().toISOString().split('T')[0], importo: 0, tipo: "banca_carta",
      banca_origine_id: banche[0]?.id || "", banca_destinazione_id: "",
      carta_origine_id: "", carta_destinazione_id: carte[0]?.id || "", descrizione: "", note: ""
    });
    setGirocontoDialog(true);
  };

  const saveGiroconto = async () => {
    if (!girocontoForm.importo || girocontoForm.importo <= 0) { handleApiError({ message: t("errors.requiredValidAmount") }); return; }
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/giroconti`, girocontoForm);
      showSuccess(t("success.transferCreatedBank"));
      setGirocontoDialog(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  const deleteGiroconto = async (id) => {
    if (!confirm(t("confirm.deleteTransfer"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/giroconti/${id}`);
      showSuccess(t("success.transferDeletedBank"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.operationFailed"));
    }
  };

  // Columns
  const bancheColumns = [
    { key: "nome", label: "Nome", sortable: true },
    { key: "iban", label: "IBAN", render: (v) => v || "-" },
    { key: "saldo_iniziale", label: "Saldo Iniziale", render: (v) => `€ ${(v || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}` },
    { key: "saldo_corrente", label: "Saldo Corrente", render: (v) => (
      <span className={v >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
        € {(v || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
      </span>
    )},
    { key: "attivo", label: "Stato", render: (v) => v ? <Badge variant="success">{t('common.active')}</Badge> : <Badge variant="secondary">Disattivata</Badge> },
    { key: "actions", label: "", render: (_, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => openBancaDialog(row)}><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => deleteBanca(row.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
    )}
  ];

  const carteColumns = [
    { key: "nome", label: "Nome", sortable: true },
    { key: "numero_carta", label: "Numero", render: (v) => v ? `****${v}` : "-" },
    { key: "tipologia", label: "Tipo", render: (v) => v || "-" },
    { key: "banca_nome", label: "Banca", render: (v) => v || "-" },
    { key: "saldo_corrente", label: "Saldo", render: (v) => (
      <span className={v >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
        € {(v || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}
      </span>
    )},
    { key: "data_scadenza", label: "Scadenza", render: (v, row) => (
      <div className="flex items-center gap-1">
        {v || "-"}
        {row.scaduta && <AlertTriangle className="h-4 w-4 text-red-500" />}
      </div>
    )},
    { key: "actions", label: "", render: (_, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => openCartaDialog(row)}><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => deleteCarta(row.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
    )}
  ];

  const girocontiColumns = [
    { key: "data", label: "Data", sortable: true },
    { key: "tipo", label: "Tipo", render: (v) => {
      const labels = { banca_banca: t("treasury.bankToBank"), banca_carta: t("treasury.bankToCard"), carta_banca: t("treasury.cardToBank") };
      return <Badge variant="outline">{labels[v] || v}</Badge>;
    }},
    { key: "importo", label: "Importo", render: (v) => `€ ${(v || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}` },
    { key: "origine", label: "Origine", render: (_, row) => row.banca_origine_nome || row.carta_origine_nome || "-" },
    { key: "destinazione", label: "Destinazione", render: (_, row) => row.banca_destinazione_nome || row.carta_destinazione_nome || "-" },
    { key: "descrizione", label: "Descrizione", render: (v) => v || "-" },
    { key: "actions", label: "", render: (_, row) => (
      <Button variant="ghost" size="sm" onClick={() => deleteGiroconto(row.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
    )}
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tesoreria-anagrafiche-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.banksCards")}</h1>
          <p className="text-muted-foreground text-sm">Gestisci banche, carte prepagate e giroconti</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="h-4 w-4" />{t("common.update")}</Button>
      </div>

      {/* Riepilogo Saldi */}
      {riepilogo && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/20 hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">{t("treasury.totalBanks")}</p>
                  <p className="text-2xl font-bold font-mono text-blue-600 mt-1">
                    € {riepilogo.totale_banche?.toLocaleString('it-IT', {minimumFractionDigits: 2})}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-950/20 hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">{t("treasury.totalCards")}</p>
                  <p className="text-2xl font-bold font-mono text-purple-600 mt-1">
                    € {riepilogo.totale_carte?.toLocaleString('it-IT', {minimumFractionDigits: 2})}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Disponibilità Totale</p>
                  <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">
                    € {riepilogo.totale_disponibilita?.toLocaleString('it-IT', {minimumFractionDigits: 2})}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Wallet className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="banche" className="space-y-4">
        <TabsList>
          <TabsTrigger value="banche" className="gap-2"><Building2 className="h-4 w-4" />Banche ({banche.length})</TabsTrigger>
          <TabsTrigger value="carte" className="gap-2"><CreditCard className="h-4 w-4" />Carte ({carte.length})</TabsTrigger>
          <TabsTrigger value="giroconti" className="gap-2"><ArrowLeftRight className="h-4 w-4" />Giroconti ({giroconti.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="banche">
          <Card>
            <CardHeader>
              <CardTitle>Conti Correnti Bancari</CardTitle>
              <CardDescription>Gestisci i conti correnti aziendali</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={bancheColumns}
                data={banche}
                idField="id"
                emptyMessage="Nessuna banca registrata"
                toolbar={<Button onClick={() => openBancaDialog()}><Plus className="h-4 w-4 mr-2" />Nuova Banca</Button>}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="carte">
          <Card>
            <CardHeader>
              <CardTitle>Carte Prepagate</CardTitle>
              <CardDescription>Gestisci le carte prepagate aziendali</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={carteColumns}
                data={carte}
                idField="id"
                emptyMessage="Nessuna carta registrata"
                toolbar={<Button onClick={() => openCartaDialog()}><Plus className="h-4 w-4 mr-2" />Nuova Carta</Button>}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="giroconti">
          <Card>
            <CardHeader>
              <CardTitle>{t('common.transfers')}</CardTitle>
              <CardDescription>Trasferimenti tra conti correnti e carte</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={girocontiColumns}
                data={giroconti}
                idField="id"
                emptyMessage="Nessun giroconto registrato"
                toolbar={<Button onClick={openGirocontoDialog}><Plus className="h-4 w-4 mr-2" />Nuovo Giroconto</Button>}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Banca */}
      <Dialog open={bancaDialog} onOpenChange={setBancaDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBanca ? t("treasury.editBank") : t("treasury.newBank")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.name')} *</Label>
                <Input value={bancaForm.nome} onChange={(e) => setBancaForm({...bancaForm, nome: e.target.value})} placeholder="Es. Banca Intesa" />
              </div>
              <div className="space-y-2">
                <Label>Filiale</Label>
                <Input value={bancaForm.filiale} onChange={(e) => setBancaForm({...bancaForm, filiale: e.target.value})} placeholder="Es. Milano Centro" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input value={bancaForm.iban} onChange={(e) => setBancaForm({...bancaForm, iban: e.target.value})} placeholder="IT60X0542811101000000123456" />
            </div>
            <div className="space-y-2">
              <Label>BIC/SWIFT</Label>
              <Input value={bancaForm.bic_swift} onChange={(e) => setBancaForm({...bancaForm, bic_swift: e.target.value})} placeholder="BCITITMM" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Saldo Iniziale (€)</Label>
                <Input type="number" step="0.01" value={bancaForm.saldo_iniziale} onChange={(e) => setBancaForm({...bancaForm, saldo_iniziale: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Data Saldo Iniziale</Label>
                <Input type="date" value={bancaForm.data_saldo_iniziale} onChange={(e) => setBancaForm({...bancaForm, data_saldo_iniziale: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={bancaForm.note} onChange={(e) => setBancaForm({...bancaForm, note: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBancaDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveBanca}>{editingBanca ? t("common.save") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Carta */}
      <Dialog open={cartaDialog} onOpenChange={setCartaDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCarta ? t("treasury.editCard") : t("treasury.newCard")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.name')} *</Label>
                <Input value={cartaForm.nome} onChange={(e) => setCartaForm({...cartaForm, nome: e.target.value})} placeholder="Es. Carta Aziendale" />
              </div>
              <div className="space-y-2">
                <Label>Ultime 4 cifre</Label>
                <Input value={cartaForm.numero_carta} onChange={(e) => setCartaForm({...cartaForm, numero_carta: e.target.value})} placeholder="1234" maxLength={4} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.typology')}</Label>
                <Select value={cartaForm.tipologia} onValueChange={(v) => setCartaForm({...cartaForm, tipologia: v})}>
                  <SelectTrigger><SelectValue placeholder={t("common.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Visa">Visa</SelectItem>
                    <SelectItem value="Mastercard">Mastercard</SelectItem>
                    <SelectItem value="American Express">American Express</SelectItem>
                    <SelectItem value="Altro">{t('common.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Banca di Appoggio</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "Nessuna" },
                    ...banche.map(b => ({ value: b.id, label: b.nome }))
                  ]}
                  value={cartaForm.banca_id || "none"}
                  onValueChange={(v) => setCartaForm({...cartaForm, banca_id: v === "none" ? "" : v})}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  testId="select-banca-carta"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Saldo Iniziale (€)</Label>
                <Input type="number" step="0.01" value={cartaForm.saldo_iniziale} onChange={(e) => setCartaForm({...cartaForm, saldo_iniziale: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Data Saldo Iniziale</Label>
                <Input type="date" value={cartaForm.data_saldo_iniziale} onChange={(e) => setCartaForm({...cartaForm, data_saldo_iniziale: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.dueDate')}</Label>
              <Input type="date" value={cartaForm.data_scadenza} onChange={(e) => setCartaForm({...cartaForm, data_scadenza: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={cartaForm.note} onChange={(e) => setCartaForm({...cartaForm, note: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCartaDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveCarta}>{editingCarta ? t("common.save") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Giroconto */}
      <Dialog open={girocontoDialog} onOpenChange={setGirocontoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("treasury.newTransfer")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input type="date" value={girocontoForm.data} onChange={(e) => setGirocontoForm({...girocontoForm, data: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input type="number" step="0.01" value={girocontoForm.importo} onChange={(e) => setGirocontoForm({...girocontoForm, importo: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo Giroconto *</Label>
              <Select value={girocontoForm.tipo} onValueChange={(v) => setGirocontoForm({...girocontoForm, tipo: v, banca_origine_id: "", banca_destinazione_id: "", carta_origine_id: "", carta_destinazione_id: ""})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="banca_banca">Banca → Banca</SelectItem>
                  <SelectItem value="banca_carta">Banca → Carta (Ricarica)</SelectItem>
                  <SelectItem value="carta_banca">Carta → Banca</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {girocontoForm.tipo === "banca_banca" && (
              <>
                <div className="space-y-2">
                  <Label>Banca Origine *</Label>
                  <SearchableSelect
                    options={banche.filter(b => b.attivo).map(b => ({ value: b.id, label: `${b.nome} (€ ${b.saldo_corrente?.toLocaleString('it-IT')})` }))}
                    value={girocontoForm.banca_origine_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, banca_origine_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-banca-origine-bb"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Banca Destinazione *</Label>
                  <SearchableSelect
                    options={banche.filter(b => b.attivo && b.id !== girocontoForm.banca_origine_id).map(b => ({ value: b.id, label: b.nome }))}
                    value={girocontoForm.banca_destinazione_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, banca_destinazione_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-banca-dest-bb"
                  />
                </div>
              </>
            )}

            {girocontoForm.tipo === "banca_carta" && (
              <>
                <div className="space-y-2">
                  <Label>Banca Origine *</Label>
                  <SearchableSelect
                    options={banche.filter(b => b.attivo).map(b => ({ value: b.id, label: `${b.nome} (€ ${b.saldo_corrente?.toLocaleString('it-IT')})` }))}
                    value={girocontoForm.banca_origine_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, banca_origine_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-banca-origine-bc"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Carta Destinazione *</Label>
                  <SearchableSelect
                    options={carte.filter(c => c.attivo).map(c => ({ value: c.id, label: `${c.nome} (€ ${c.saldo_corrente?.toLocaleString('it-IT')})` }))}
                    value={girocontoForm.carta_destinazione_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, carta_destinazione_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-carta-dest-bc"
                  />
                </div>
              </>
            )}

            {girocontoForm.tipo === "carta_banca" && (
              <>
                <div className="space-y-2">
                  <Label>Carta Origine *</Label>
                  <SearchableSelect
                    options={carte.filter(c => c.attivo).map(c => ({ value: c.id, label: `${c.nome} (€ ${c.saldo_corrente?.toLocaleString('it-IT')})` }))}
                    value={girocontoForm.carta_origine_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, carta_origine_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-carta-origine-cb"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Banca Destinazione *</Label>
                  <SearchableSelect
                    options={banche.filter(b => b.attivo).map(b => ({ value: b.id, label: b.nome }))}
                    value={girocontoForm.banca_destinazione_id}
                    onValueChange={(v) => setGirocontoForm({...girocontoForm, banca_destinazione_id: v})}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    testId="select-banca-dest-cb"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Input value={girocontoForm.descrizione} onChange={(e) => setGirocontoForm({...girocontoForm, descrizione: e.target.value})} placeholder="Es. Ricarica carta aziendale" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGirocontoDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveGiroconto}>Registra Giroconto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
